// Gateway/unavailable statuses a reverse proxy returns while the backend is restarting or
// mid-deploy. The request never reached (or wasn't processed by) the app, so retrying is safe.
// A plain 500 is a real application error — not retried.
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

// The app also answers 502 (KRA upstream failed) and 503 (KRA breaker open) itself, always with a
// JSON body. Those requests were processed — replaying them re-runs the KRA call. A proxy's own
// error page is HTML or empty.
// ponytail: content-type sniff; a response header set by the app is the upgrade if a proxy ever answers JSON.
function cameFromProxy(response: Response): boolean {
  return !(response.headers.get("content-type") ?? "").includes("json");
}

// Escalating backoff sized to bridge a typical service restart / rolling deploy (~10s of
// unavailability) while staying bounded, so a genuinely-down backend surfaces the error instead of
// hanging forever. Attempt count = BACKOFFS.length + 1. Total worst-case wait ≈ 10.5s.
const BACKOFFS_MS = [500, 1000, 2000, 3000, 4000];

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function isIdempotent(method: string | undefined): boolean {
  const m = (method ?? "GET").toUpperCase();
  return m === "GET" || m === "HEAD";
}

/**
 * `fetch` that transparently rides out transient backend unavailability (deployments, restarts) so
 * the app recovers silently instead of flashing an error page.
 *
 * Retries with backoff on:
 *  - a {@link TRANSIENT_STATUSES} response (502/503/504) from the proxy, not the app — for any method,
 *    since the request wasn't processed by the app;
 *  - a thrown connection error — for idempotent methods (GET/HEAD) only. A POST/PUT that throws may
 *    have reached the server, so it is never auto-replayed (avoids double submits).
 *
 * Healthy responses (2xx) and terminal ones (4xx, 500) return on the first attempt. When retries are
 * exhausted the last response/error is returned/thrown unchanged, so existing error handling still runs.
 */
export async function resilientFetch(
  url: string,
  init: RequestInit = {},
  // Overridable only so tests run instantly; production always uses the escalating backoff above.
  backoffsMs: readonly number[] = BACKOFFS_MS,
): Promise<Response> {
  const idempotent = isIdempotent(init.method);
  const maxAttempts = backoffsMs.length + 1;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const isLastAttempt = attempt === maxAttempts - 1;
    try {
      const response = await fetch(url, init);
      if (!isLastAttempt && TRANSIENT_STATUSES.has(response.status) && cameFromProxy(response)) {
        await delay(backoffsMs[attempt]!);
        continue;
      }
      return response;
    } catch (error) {
      lastError = error;
      if (!isLastAttempt && idempotent) {
        await delay(backoffsMs[attempt]!);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

/** True when a refresh/request failure is backend-unavailability (recoverable), not an auth/terminal error. */
export function isTransientStatus(status: number): boolean {
  return TRANSIENT_STATUSES.has(status);
}
